import { StatusModal } from '@/components/StatusModal';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useCallback, useEffect, useState } from 'react';

type LogRow = {
  id: number;
  order_code: string;
  from_status: string;
  to_status: string;
  term_date: string;
  ok: number;
  error: string | null;
  created_at: number;
};

type Phase = {
  checked: number;
  moved: number;
  skipped: number;
  errors: string[];
  orders: { code: string; from: string; to: string; term_date: string }[];
};

type RunResult = {
  dry_run: boolean;
  /** Zařazení nových objednávek do termínů. */
  sorted: Phase;
  /** Vypuštění došitých objednávek do „Vyřizuje se". */
  released: Phase;
};

type ExpediceAuditRow = {
  sku: string;
  product_name: string;
  required: number;
  real_qty: number;
  reservations: number;
  shortfall: number;
  orders: { eshop: string; code: string; status: string; qty: number }[];
};

type ExpediceAuditResult = {
  checked_orders: number;
  skus_checked: number;
  problems: ExpediceAuditRow[];
  errors: string[];
};

/** Unix ms → „3.8.2026 21:15". */
function fmtDateTime(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 'YYYY-MM-DD' → '31.7.2026'. */
function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${Number(d)}.${Number(m)}.${y}` : iso;
}

/**
 * Log automatu, který posouvá došité předobjednávky ze stavu
 * „Došíváme - odeslat …" do „Vyřizuje se". Běží každých 15 minut; odsud jde
 * i spustit ručně — nejdřív nanečisto, pak naostro.
 */
export function LogObjednavekPage() {
  const [rows, setRows] = useState<LogRow[] | null>(null);
  const [run, setRun] = useState<RunResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState<ExpediceAuditResult | null>(null);
  const [auditBusy, setAuditBusy] = useState(false);
  // Ruční odškrtávání vyřešených SKU — jen vizuální pomůcka, nic se neukládá.
  const [auditResolved, setAuditResolved] = useState<Set<string>>(new Set());
  const toggleAuditResolved = (sku: string) =>
    setAuditResolved((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku);
      else next.add(sku);
      return next;
    });
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch<{ rows: LogRow[] }>('/api/vyroba/objednavky/log');
      setRows(d.rows);
    } catch (e: unknown) {
      setRows([]);
      setToast({
        msg: e instanceof ApiError ? e.describe() : 'Načtení logu selhalo',
        kind: 'error',
      });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function spustit(live: boolean) {
    setBusy(true);
    setRun(null);
    try {
      const d = await apiFetch<RunResult>(
        `/api/vyroba/objednavky/predobjednavky${live ? '?live=1' : ''}`,
      );
      setRun(d);
      if (live) {
        setToast({
          msg: `Zařazeno ${d.sorted.moved}, vypuštěno ${d.released.moved} objednávek`,
          kind: 'success',
        });
        await load();
      }
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.describe() : 'Spuštění selhalo', kind: 'error' });
    } finally {
      setBusy(false);
    }
  }

  async function spustitAudit() {
    setAuditBusy(true);
    setAudit(null);
    setAuditResolved(new Set());
    try {
      const d = await apiFetch<ExpediceAuditResult>('/api/vyroba/expedice/audit', {
        method: 'POST',
      });
      setAudit(d);
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.describe() : 'Audit selhal', kind: 'error' });
    } finally {
      setAuditBusy(false);
    }
  }

  return (
    <>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-text">Log automatizace objednávek</h1>
          <p className="text-sm text-text-secondary mt-0.5 max-w-3xl">
            Automat každých 5 minut zařadí nové objednávky do termínu podle toho, do kdy se jejich
            kusy šijí, a objednávky, na jejichž kusy už se nečeká, přepne na „Vyřizuje se".
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            onClick={() => spustit(false)}
            disabled={busy}
            className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover disabled:opacity-50 cursor-pointer transition-colors"
          >
            Zkusit nanečisto
          </button>
          <button
            type="button"
            onClick={() => spustit(true)}
            disabled={busy}
            className="bg-action hover:bg-action-dark disabled:opacity-50 text-white text-sm font-bold rounded-lg px-5 py-2.5 cursor-pointer transition-colors"
          >
            {busy ? 'Kontroluji…' : 'Spustit teď'}
          </button>
        </div>
      </div>

      <div className="mb-5 rounded-xl border border-border bg-surface px-5 py-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-sm font-bold text-text">Audit expedice</div>
            <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
              Mají všechny objednávky ve stavu „Vyřizuje se" (CZ i SK) reálně dost kusů v Brani?
              Sečte položky napříč objednávkami a porovná je s fyzickou skladovostí — ne s
              Shoptetovým číslem ani s Braniho rezervacemi.
            </p>
          </div>
          <button
            type="button"
            onClick={spustitAudit}
            disabled={auditBusy}
            className="bg-action hover:bg-action-dark disabled:opacity-50 text-white text-sm font-bold rounded-lg px-5 py-2.5 cursor-pointer transition-colors shrink-0"
          >
            {auditBusy ? 'Kontroluji…' : 'Zkontrolovat expedici'}
          </button>
        </div>

        {audit && (
          <div className="mt-4">
            <div className="text-sm text-text-secondary mb-2">
              Zkontrolováno {audit.checked_orders} objednávek, {audit.skus_checked} různých SKU.
              {audit.problems.length > 0 && ' Klikni na řádek, až ho vyřešíš.'}
            </div>
            {audit.errors.length > 0 && (
              <ul className="mb-3 space-y-0.5 text-[13px] text-danger">
                {audit.errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            )}
            {audit.problems.length === 0 ? (
              <div className="text-sm font-semibold text-success">
                ✓ Vše sedí — na žádné SKU nechybí kusy.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-bg text-left text-xs font-semibold text-text-secondary">
                      <th className="px-3 py-2">SKU</th>
                      <th className="px-3 py-2">Produkt</th>
                      <th className="px-3 py-2 text-right">Potřeba</th>
                      <th className="px-3 py-2 text-right">Reálně v Brani</th>
                      <th className="px-3 py-2 text-right">Chybí</th>
                      <th className="px-3 py-2">Objednávky</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.problems.map((p) => (
                      // biome-ignore lint/a11y/useKeyWithClickEvents: řádek tabulky, klik jen odškrtne vyřešené (interní nástroj)
                      <tr
                        key={p.sku}
                        onClick={() => toggleAuditResolved(p.sku)}
                        className={`border-b border-border-light align-top cursor-pointer transition-colors ${
                          auditResolved.has(p.sku)
                            ? 'bg-success-light hover:bg-success-light'
                            : 'hover:bg-surface-hover'
                        }`}
                      >
                        <td className="px-3 py-1.5 font-mono text-text whitespace-nowrap">
                          {p.sku}
                        </td>
                        <td className="px-3 py-1.5 text-text-secondary">{p.product_name}</td>
                        <td className="px-3 py-1.5 text-right text-text">{p.required}</td>
                        <td className="px-3 py-1.5 text-right text-text-secondary">
                          {p.real_qty}
                          {p.reservations > 0 && (
                            <span className="text-text-muted"> (nárok {p.reservations})</span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-right font-bold text-danger">
                          {p.shortfall}
                        </td>
                        <td className="px-3 py-1.5 text-[13px] text-text-secondary">
                          {p.orders.map((o) => `${o.code} (${o.eshop}, ${o.qty} ks)`).join(' · ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {run && (
        <div className="mb-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <PhaseCard
            title="Zařazení do termínů"
            dry={run.dry_run}
            phase={run.sorted}
            fmtDate={fmtDate}
          />
          <PhaseCard
            title={'Vypuštění do „Vyřizuje se"'}
            dry={run.dry_run}
            phase={run.released}
            fmtDate={fmtDate}
          />
        </div>
      )}

      <div className="bg-surface rounded-xl shadow-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-bg text-left text-xs font-semibold text-text-secondary">
              <th className="px-4 py-2.5">Kdy</th>
              <th className="px-4 py-2.5">Objednávka</th>
              <th className="px-4 py-2.5">Z termínového stavu</th>
              <th className="px-4 py-2.5">Termín</th>
              <th className="px-4 py-2.5">Nový stav</th>
              <th className="px-4 py-2.5">Výsledek</th>
            </tr>
          </thead>
          <tbody>
            {rows == null ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-text-muted">
                  Načítám…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-text-muted">
                  Automat zatím nic nepřepnul.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="border-b border-border-light">
                  <td className="px-4 py-1.5 text-text-secondary whitespace-nowrap">
                    {fmtDateTime(r.created_at)}
                  </td>
                  <td className="px-4 py-1.5 font-semibold text-text whitespace-nowrap">
                    {r.order_code}
                  </td>
                  <td className="px-4 py-1.5 text-text-secondary">{r.from_status}</td>
                  <td className="px-4 py-1.5 text-text-secondary whitespace-nowrap">
                    {fmtDate(r.term_date)}
                  </td>
                  <td className="px-4 py-1.5 text-text-secondary">{r.to_status}</td>
                  <td className="px-4 py-1.5">
                    {r.ok ? (
                      <span className="text-sm font-semibold text-success">✓ přepnuto</span>
                    ) : (
                      <span className="text-sm font-semibold text-danger" title={r.error ?? ''}>
                        ✕ {r.error ?? 'chyba'}
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {toast && (
        <StatusModal message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />
      )}
    </>
  );
}

/** Souhrn jedné fáze běhu — kolik se zkontrolovalo, přepnulo a co selhalo. */
function PhaseCard({
  title,
  dry,
  phase,
  fmtDate,
}: {
  title: string;
  dry: boolean;
  phase: Phase;
  fmtDate: (iso: string) => string;
}) {
  return (
    <div className="rounded-xl border border-border bg-surface px-5 py-4">
      <div className="text-sm font-bold text-text">{title}</div>
      <div className="mt-1 text-sm text-text-secondary">
        zkontrolováno {phase.checked}, {dry ? 'k přepnutí' : 'přepnuto'}{' '}
        <strong className="font-semibold text-text">
          {dry ? phase.orders.length : phase.moved}
        </strong>
        , beze změny {phase.skipped}
      </div>
      {phase.orders.length > 0 && (
        <div className="mt-2 text-[13px] text-text-secondary">
          {phase.orders.map((o) => `${o.code} (${fmtDate(o.term_date)})`).join(' · ')}
        </div>
      )}
      {phase.errors.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-[13px] text-danger">
          {phase.errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
